import "./errors";

export type FeatureBootstrap = {
  key: string;
  selector: string;
  mount(root: HTMLElement): void;
  unmount?(root: HTMLElement): void;
};

export type FrontendBootstrapRegistryAPI = {
  register(feature: FeatureBootstrap): void;
  mount(root?: ParentNode | null): void;
  unmount(root?: ParentNode | null): void;
  list(): string[];
  has(key: string): boolean;
};

type WindowWithBootstrap = Window & {
  WMUBootstrap?: FrontendBootstrapRegistryAPI;
  __WMU_BOOTSTRAP_READY?: boolean;
};

type RegistryInternals = {
  features: Map<string, FeatureBootstrap>;
  mountedByRoot: WeakMap<HTMLElement, Set<string>>;
  observer: MutationObserver | null;
};

function getDefaultRoot(doc: Document): ParentNode | null {
  return doc.body || doc.documentElement;
}

function canQuery(root: ParentNode): root is ParentNode & Pick<ParentNode, "querySelectorAll"> {
  return typeof (root as Pick<ParentNode, "querySelectorAll">).querySelectorAll === "function";
}

function matchesSelector(node: HTMLElement, selector: string): boolean {
  try {
    return node.matches(selector);
  } catch (_error) {
    return false;
  }
}

function collectMatches(root: ParentNode, selector: string): HTMLElement[] {
  const matches: HTMLElement[] = [];
  if (root instanceof HTMLElement && matchesSelector(root, selector)) {
    matches.push(root);
  }
  if (!canQuery(root)) {
    return matches;
  }
  root.querySelectorAll(selector).forEach((node) => {
    if (node instanceof HTMLElement) {
      matches.push(node);
    }
  });
  return matches;
}

function mountedKeysFor(internals: RegistryInternals, root: HTMLElement): Set<string> {
  const existing = internals.mountedByRoot.get(root);
  if (existing) {
    return existing;
  }
  const next = new Set<string>();
  internals.mountedByRoot.set(root, next);
  return next;
}

function isMounted(internals: RegistryInternals, root: HTMLElement, key: string): boolean {
  return internals.mountedByRoot.get(root)?.has(key) === true;
}

function markMounted(internals: RegistryInternals, root: HTMLElement, key: string): void {
  mountedKeysFor(internals, root).add(key);
}

function markUnmounted(internals: RegistryInternals, root: HTMLElement, key: string): void {
  internals.mountedByRoot.get(root)?.delete(key);
}

function resolveLifecycleRoot(event: Event, doc: Document): ParentNode | null {
  const detail = event instanceof CustomEvent && typeof event.detail === "object" && event.detail
    ? event.detail as { target?: EventTarget | null; elt?: EventTarget | null }
    : null;
  if (detail?.target instanceof HTMLElement) {
    return detail.target;
  }
  if (detail?.elt instanceof HTMLElement) {
    return detail.elt;
  }
  if (event.target instanceof HTMLElement) {
    return event.target;
  }
  return getDefaultRoot(doc);
}

function createRegistry(win: WindowWithBootstrap): FrontendBootstrapRegistryAPI {
  const doc = win.document;
  const internals: RegistryInternals = {
    features: new Map<string, FeatureBootstrap>(),
    mountedByRoot: new WeakMap<HTMLElement, Set<string>>(),
    observer: null,
  };

  const mountFeature = (feature: FeatureBootstrap, root: ParentNode): void => {
    collectMatches(root, feature.selector).forEach((element) => {
      if (isMounted(internals, element, feature.key)) {
        return;
      }
      feature.mount(element);
      markMounted(internals, element, feature.key);
    });
  };

  const unmountFeature = (feature: FeatureBootstrap, root: ParentNode): void => {
    const matches = collectMatches(root, feature.selector);
    for (let index = matches.length - 1; index >= 0; index -= 1) {
      const element = matches[index];
      if (!(element instanceof HTMLElement)) {
        continue;
      }
      if (!isMounted(internals, element, feature.key)) {
        continue;
      }
      try {
        feature.unmount?.(element);
      } finally {
        markUnmounted(internals, element, feature.key);
      }
    }
  };

  const observeRemovals = (): void => {
    if (internals.observer || !(doc.body instanceof HTMLElement)) {
      return;
    }
    internals.observer = new MutationObserver((records) => {
      records.forEach((record) => {
        record.removedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            api.unmount(node);
          }
        });
      });
    });
    internals.observer.observe(doc.body, { childList: true, subtree: true });
  };

  const boot = (): void => {
    if (win.__WMU_BOOTSTRAP_READY) {
      return;
    }
    win.__WMU_BOOTSTRAP_READY = true;
    const root = getDefaultRoot(doc);
    if (root) {
      api.mount(root);
      observeRemovals();
    }

    doc.addEventListener("htmx:beforeSwap", (event) => {
      api.unmount(resolveLifecycleRoot(event, doc));
    });

    doc.addEventListener("htmx:beforeCleanupElement", (event) => {
      api.unmount(resolveLifecycleRoot(event, doc));
    });

    doc.addEventListener("htmx:afterSwap", (event) => {
      api.mount(resolveLifecycleRoot(event, doc));
    });

    win.addEventListener("pagehide", () => {
      api.unmount(getDefaultRoot(doc));
    });
  };

  const api: FrontendBootstrapRegistryAPI = {
    register(feature: FeatureBootstrap): void {
      const key = String(feature.key || "").trim();
      const selector = String(feature.selector || "").trim();
      if (!key || !selector) {
        throw new Error("WMUBootstrap.register requires a feature key and selector.");
      }
      const previous = internals.features.get(key);
      const root = getDefaultRoot(doc);
      if (previous && root) {
        unmountFeature(previous, root);
      }
      internals.features.set(key, { ...feature, key, selector });
      if (win.__WMU_BOOTSTRAP_READY && root) {
        mountFeature(feature, root);
      }
    },

    mount(root?: ParentNode | null): void {
      const nextRoot = root || getDefaultRoot(doc);
      if (!nextRoot) {
        return;
      }
      internals.features.forEach((feature) => {
        mountFeature(feature, nextRoot);
      });
    },

    unmount(root?: ParentNode | null): void {
      const nextRoot = root || getDefaultRoot(doc);
      if (!nextRoot) {
        return;
      }
      const features = Array.from(internals.features.values()).reverse();
      features.forEach((feature) => {
        unmountFeature(feature, nextRoot);
      });
    },

    list(): string[] {
      return Array.from(internals.features.keys()).sort();
    },

    has(key: string): boolean {
      return internals.features.has(String(key || "").trim());
    },
  };

  if (doc.readyState === "loading") {
    doc.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  return api;
}

function bootstrapRegistry(): FrontendBootstrapRegistryAPI {
  const win = window as WindowWithBootstrap;
  if (!win.WMUBootstrap) {
    win.WMUBootstrap = createRegistry(win);
  }
  return win.WMUBootstrap;
}

export function registerFeatureBootstrap(feature: FeatureBootstrap): void {
  bootstrapRegistry().register(feature);
}

export function mountBootstrapRoot(root?: ParentNode | null): void {
  bootstrapRegistry().mount(root);
}

export function unmountBootstrapRoot(root?: ParentNode | null): void {
  bootstrapRegistry().unmount(root);
}

window.WMUBootstrap = bootstrapRegistry();
